import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Loader2 } from "lucide-react";

// Mobile: native share sheet with the image. Desktop: download image + open WhatsApp Web with text.
async function shareToWhatsApp(file, text) {
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  if (isTouch && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], text });
    return "idle";
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  return "downloaded";
}

export default function WhatsAppShareButton({ getFile, text, labels, testId, className = "" }) {
  const fileRef = useRef(null);
  const [status, setStatus] = useState("idle");

  const load = useCallback(async () => {
    if (!fileRef.current) fileRef.current = await getFile();
    return fileRef.current;
  }, [getFile]);

  // Pre-build so the mobile share sheet opens instantly on tap.
  useEffect(() => {
    const t = setTimeout(() => load().catch(() => {}), 1500);
    return () => clearTimeout(t);
  }, [load]);

  const onClick = async () => {
    setStatus("working");
    try {
      setStatus(await shareToWhatsApp(await load(), text));
    } catch (e) {
      setStatus(e?.name === "AbortError" ? "idle" : "error");
    }
  };

  const note = status === "downloaded" ? labels.downloadedNote : status === "error" ? labels.errorNote : "";

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <button
        type="button"
        onClick={onClick}
        disabled={status === "working"}
        data-testid={testId}
        className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-7 py-3.5 font-poppins font-semibold text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-electric hover:border-electric hover:shadow-[0_0_28px_rgba(33,112,232,0.55)] disabled:opacity-60 cursor-pointer"
      >
        {status === "working" ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <MessageCircle size={18} className="text-[#25D366] transition-colors duration-300 group-hover:text-white" />
        )}
        {status === "working" ? labels.workingLabel : labels.label}
      </button>
      {note && (
        <p className="max-w-sm font-poppins text-xs md:text-sm text-ceramic/70" data-testid={`${testId}-note`}>
          {note}
        </p>
      )}
    </div>
  );
}
